import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { OptionsPageShell } from './OptionsPageShell';
import { OptionsOnboardingCard } from './OptionsOnboardingCard';
import { useOptionsOverviewState } from '../../hooks/useOptionsOverviewState';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import GeniLoader from '../../components/GeniLoader';

export const OptionsPlaceholderPage = ({
  title,
  description,
  eyebrow,
  placeholderTitle,
  placeholderDescription,
  bullets,
}) => {
  const { t } = useOptionsLocale();
  const { connected, loading } = useOptionsOverviewState();

  if (loading) {
    return (
      <OptionsPageShell title={title} description={description} eyebrow={eyebrow} hideHeader>
        <Card className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid="options-placeholder-loading-card">
          <CardContent className="p-6" data-testid="options-placeholder-loading-text">
            <div className="min-h-[240px]">
              <GeniLoader size="lg" />
            </div>
          </CardContent>
        </Card>
      </OptionsPageShell>
    );
  }

  return (
    <OptionsPageShell title={title} description={description} eyebrow={eyebrow} hideHeader>
      {!connected ? (
        <OptionsOnboardingCard
          title={t('onboarding.title')}
          description={t('onboarding.description')}
          connectLabel={t('common.connectDeribit')}
          securityTitle={t('onboarding.securityTitle')}
          securityBody={t('onboarding.securityBody')}
          securityDetails={t('onboarding.securityDetails')}
        />
      ) : (
        <Card className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid="options-placeholder-card">
          <CardContent className="space-y-6 p-6 sm:p-8">
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-white/45" data-testid="options-placeholder-eyebrow">{eyebrow}</p>
              <h2 className="text-3xl font-black font-heading tracking-tight" data-testid="options-placeholder-title">{placeholderTitle}</h2>
              <p className="max-w-3xl text-sm text-white/65 sm:text-base" data-testid="options-placeholder-description">{placeholderDescription}</p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2" data-testid="options-placeholder-bullets">
              {bullets.map((bullet) => (
                <div key={bullet} className="rounded-2xl border border-white/10 bg-black/20 px-4 py-4 text-sm text-white/70" data-testid={`options-placeholder-bullet-${bullet.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}>
                  {bullet}
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3" data-testid="options-placeholder-actions">
              <Button asChild data-testid="options-placeholder-dashboard-button">
                <Link to="/options">{t('placeholders.dashboardAction')}</Link>
              </Button>
              <Button asChild variant="outline" data-testid="options-placeholder-connect-button">
                <Link to="/options/connect">{t('placeholders.connectAction')}</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </OptionsPageShell>
  );
};

export default OptionsPlaceholderPage;